import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Button,
    Switch,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    IconButton,
    Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_URL } from '../../api';
import InvoicesTabs from '../../components/InvoicesTabs';
import { useConfirm } from '../../components/ConfirmDialog';
import { shortYear } from '../../constants/yearGroups';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const fmt = (amount) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(amount) || 0);

const CADENCE_LABELS = {
    weekly: 'Weekly',
    fortnightly: 'Fortnightly',
    monthly: 'Monthly',
    quarterly: 'Termly (3 months)',
    yearly: 'Yearly',
};

const describeSchedule = (s) => {
    if (s.kind === 'oneOff') return 'One-off';
    return CADENCE_LABELS[s.cadence] || s.cadence || 'Recurring';
};

const scheduleRecipientLabel = (s) => {
    if (s.parentId) return `${s.parentId.firstName || ''} ${s.parentId.surname || ''}`.trim();
    if (s.contactEnquiryId)
        return `${s.contactEnquiryId.firstName || ''} ${s.contactEnquiryId.surname || ''}`.trim()
            + ' (from enquiry)';
    if (s.recipientName) return s.recipientName;
    if (s.recipientEmail) return s.recipientEmail;
    return '—';
};

export default function InvoiceSchedulesPage() {
    const confirm = useConfirm();
    const [schedules, setSchedules] = useState([]);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/invoices/schedules/all`,
                authHeaders()
            );
            setSchedules(data || []);
        } catch {
            setError('Failed to load.');
        }
    };

    useEffect(() => {
        load();
    }, []);

    const toggle = async (s) => {
        await axios.patch(
            `${API_URL}/api/admin/invoices/schedules/${s._id}`,
            { active: !s.active },
            authHeaders()
        );
        await load();
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete schedule', message: 'Delete this schedule? This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return;
        await axios.delete(`${API_URL}/api/admin/invoices/schedules/${id}`, authHeaders());
        await load();
    };

    const runNow = async (id) => {
        if (!(await confirm({ title: 'Generate invoice now', message: 'Generate and send an invoice now?', confirmLabel: 'Generate & send' }))) return;
        setBusy(true);
        try {
            await axios.post(`${API_URL}/api/admin/invoices/schedules/${id}/run-now`, {}, authHeaders());
            setMessage('Invoice generated and sent.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">Invoices</Typography>
                <Button
                    component={RouterLink}
                    to="/invoice-schedules/new"
                    variant="contained"
                    sx={{
                        padding: '8px 22px',
                        fontFamily: '"Inter"',
                        fontWeight: 500,
                        color: '#fff !important',
                    }}
                >
                    Schedule invoice
                </Button>
            </Stack>

            <InvoicesTabs current="scheduled" />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

            <Card>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Recipient</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Total / run</TableCell>
                            <TableCell>Next run</TableCell>
                            <TableCell>Active</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {schedules.map((s) => {
                            const total = (s.lineItemsTemplate || []).reduce(
                                (acc, i) => acc + (i.qty || 1) * (i.unitAmount || 0),
                                0
                            );
                            const sub = [s.term, shortYear(s.yearGroup)].filter(Boolean).join(' · ');
                            return (
                                <TableRow key={s._id}>
                                    <TableCell>
                                        <Typography variant="body2">{scheduleRecipientLabel(s)}</Typography>
                                        {sub && (
                                            <Typography variant="caption" color="text.secondary">
                                                {sub}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{describeSchedule(s)}</TableCell>
                                    <TableCell>{fmt(total)}</TableCell>
                                    <TableCell>
                                        {s.nextRunAt ? new Date(s.nextRunAt).toLocaleDateString('en-GB') : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Switch checked={s.active} onChange={() => toggle(s)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => runNow(s._id)} disabled={busy}>
                                            Run now
                                        </Button>
                                        <IconButton onClick={() => remove(s._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {schedules.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                        No schedules yet. Click "Schedule invoice" to create one.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Card>
        </Container>
    );
}
